import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { Star, MapPin, ChefHat } from "lucide-react";

export function PopularSection() {
  const [data, setData] = useState(null);
  const [city, setCity] = useState("");
  const [stateSel, setStateSel] = useState("");
  const [autoTried, setAutoTried] = useState(false);

  const load = async (c, st) => {
    try {
      const params = {};
      if (c) params.city = c;
      if (st) params.state = st;
      const { data } = await api.get("/popular", { params });
      setData(data);
      return data;
    } catch (err) {
      console.error("Popular load failed:", err);
      return null;
    }
  };

  useEffect(() => {
    (async () => {
      const initial = await load();
      // Try geolocation once, only if we haven't
      if (!autoTried && initial && initial.available_cities?.length && "geolocation" in navigator) {
        setAutoTried(true);
        navigator.geolocation.getCurrentPosition(
          async (pos) => {
            try {
              const res = await fetch(
                `https://nominatim.openstreetmap.org/reverse?lat=${pos.coords.latitude}&lon=${pos.coords.longitude}&format=json`,
                { headers: { "Accept-Language": "en" } }
              );
              const j = await res.json();
              const guess = j.address?.city || j.address?.town || j.address?.village || j.address?.county;
              if (guess) {
                const match = initial.available_cities.find(
                  (x) => x.toLowerCase() === guess.toLowerCase()
                );
                if (match) { setCity(match); load(match); }
              }
            } catch (e) { /* ignore geo failures */ }
          },
          () => { /* user denied — stay default */ },
          { timeout: 4000, maximumAge: 60 * 60 * 1000 }
        );
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!data || (data.top_kitchens.length === 0 && data.top_chefs.length === 0)) return null;

  return (
    <section className="border-b hairline" data-testid="popular-section">
      <div className="max-w-7xl mx-auto px-6 py-24">
        <div className="flex items-baseline justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="tick-label">03 · Popular {data.city ? `in ${data.city}` : "right now"}</div>
            <h2 className="font-display font-black text-4xl lg:text-5xl mt-3 tracking-tight">
              Where the pros cook.
            </h2>
          </div>
          {data.available_cities?.length > 0 && (
            <div className="flex gap-2">
              {data.available_states?.length > 0 && (
                <select
                  data-testid="popular-state-select"
                  value={stateSel}
                  onChange={(e) => { setStateSel(e.target.value); setCity(""); load("", e.target.value); }}
                  className="border hairline px-4 py-2.5 focus:outline-none focus:border-[#FF3B30] uppercase text-xs tracking-widest"
                >
                  <option value="">All states</option>
                  {data.available_states.map((s) => (<option key={s} value={s}>{s}</option>))}
                </select>
              )}
              <select
                data-testid="popular-city-select"
                value={city}
                onChange={(e) => { setCity(e.target.value); load(e.target.value, stateSel); }}
                className="border hairline px-4 py-2.5 focus:outline-none focus:border-[#FF3B30] uppercase text-xs tracking-widest"
              >
                <option value="">All cities</option>
                {data.available_cities.map((c) => (<option key={c} value={c}>{c}</option>))}
              </select>
            </div>
          )}
        </div>

        {data.top_kitchens.length > 0 && (
          <>
            <div className="tick-label mb-4">Top kitchens</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {data.top_kitchens.map((k) => (
                <Link key={k.id} to={`/kitchen/${k.id}`} data-testid={`popular-kitchen-${k.id}`} className="k-card block">
                  <div className="aspect-[4/3] overflow-hidden bg-neutral-900">
                    <img
                      src={k.photos?.[0] ? fileUrl(k.photos[0]) : "https://images.unsplash.com/photo-1782750161991-23529c9462bb?crop=entropy&cs=srgb&fm=jpg&q=85"}
                      alt={k.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-baseline justify-between">
                      <h3 className="font-display font-bold text-lg truncate pr-2">{k.name}</h3>
                      <div className="font-mono text-lg font-semibold">${k.hourly_rate}</div>
                    </div>
                    <div className="mt-1 flex items-center gap-1 text-neutral-400 text-sm">
                      <MapPin size={13} /> <span>{k.city}</span>
                      {k.review_count > 0 && (<>
                        <span className="mx-1">·</span>
                        <Star size={12} className="fill-[#FF3B30] text-[#FF3B30]" />
                        <span className="font-mono">{k.rating.toFixed(1)}</span>
                      </>)}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}

        {data.top_chefs.length > 0 && (
          <>
            <div className="tick-label mb-4 mt-14">Top chefs</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {data.top_chefs.map((c) => (
                <Link key={c.id} to={`/chef/${c.id}`} data-testid={`popular-chef-${c.id}`} className="k-card p-5 flex items-center gap-4">
                  <div className="w-16 h-16 border hairline bg-neutral-900 overflow-hidden flex-shrink-0">
                    {c.avatar_url ? (
                      <img src={fileUrl(c.avatar_url)} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center"><ChefHat size={22} className="text-neutral-700" /></div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold text-lg truncate">{c.name}</div>
                    {c.specialties?.length > 0 && (
                      <div className="text-neutral-400 text-xs truncate">{c.specialties.slice(0, 3).join(" · ")}</div>
                    )}
                    {c.review_count > 0 && (
                      <div className="text-sm mt-1 flex items-center gap-1">
                        <Star size={11} className="fill-[#FF3B30] text-[#FF3B30]" />
                        <span className="font-mono">{c.rating.toFixed(1)}</span>
                        <span className="text-neutral-500">({c.review_count})</span>
                      </div>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}
